/* Cómo se agrupan los documentos de un proceso — una sola definición.
 *
 * ⚠ Se agrupa por CATEGORÍA y no por el `grupo` del clasificador, y la diferencia importa. En
 * `app/secop_api.py`, `grupo` significa «qué lee el análisis del pliego»: "Anexo / formato" está en
 * `licitacion` PORQUE el modelo tiene que leer los formularios para decir qué hay que adjuntar.
 * Agrupar la pantalla por ese campo metía Seguridad Social y Origen de Fondos bajo «Para decidir si
 * te presentas», contradiciendo la nota del propio bloque. Y cambiarlo allá para arreglarlo aquí
 * haría que el análisis dejara de leerlos, que es un precio altísimo por un rótulo.
 *
 * Así que son dos preguntas distintas con dos respuestas distintas, cada una en su sitio: allá qué
 * se LEE, aquí qué se ENSEÑA.
 *
 * Y vive en un módulo propio porque lo usan DOS pantallas —la sección 06 de la ficha (7a) y el
 * archivo completo (7c)— y dos agrupaciones distintas para los mismos documentos serían dos
 * respuestas a la misma pregunta según por dónde se llegue.
 */
import type { Documento } from './tipos'

/**
 * Los bloques de la lista, en el orden en que conviene leerlos.
 *
 * ⚠ Se agrupa por CATEGORÍA y no por el `grupo` del clasificador, y la diferencia importa. En
 * `secop_api.py`, `grupo` significa «qué lee el análisis del pliego»: "Anexo / formato" está en
 * `licitacion` PORQUE el modelo tiene que leer los formularios para decir qué hay que adjuntar.
 * Agrupar la ficha por ese campo metía Seguridad Social y Origen de Fondos bajo «Para decidir si te
 * presentas», contradiciendo la nota del propio bloque. Y cambiarlo allá para arreglarlo aquí haría
 * que el análisis dejara de leerlos, que es un precio altísimo por un rótulo.
 *
 * Así que son dos preguntas distintas con dos respuestas distintas, cada una en su sitio: allá qué
 * se lee, aquí qué se enseña. El `grupo` queda de red por si aparece una categoría nueva.
 */
export const BLOQUES: { titulo: string; nota: string; cats: string[]; grupos: string[] }[] = [
  {
    titulo: 'Para decidir si te presentas',
    nota: 'el pliego, los estudios previos, los anexos técnicos y las adendas',
    cats: ['Pliego de condiciones', 'Estudios previos', 'Análisis del sector',
           'Anexo / soporte técnico', 'Aviso / Invitación', 'Matriz de riesgos', 'Cronograma',
           'Adenda', 'Respuesta a observaciones'],
    grupos: ['licitacion'],
  },
  {
    titulo: 'Lo que tendrías que presentar',
    nota: 'formatos, formularios y documentos de la empresa',
    cats: ['Anexo / formato', 'Oferta / Propuesta', 'Documentos del contratista'],
    grupos: ['oferta'],
  },
  {
    titulo: 'El contrato y sus garantías',
    nota: 'minuta, pólizas y modificaciones',
    cats: ['Contrato / Minuta', 'Garantía / Póliza'],
    grupos: ['contractual'],
  },
  {
    titulo: 'Cómo va el proceso',
    nota: 'evaluación de ofertas y adjudicación — sirven para saber en qué punto está',
    cats: ['Evaluación de ofertas', 'Adjudicación'],
    grupos: [],
  },
]

/** Cuántos documentos hay en cada tercio del titular. Lo usan la sección y el pie. */
export function repartoDe(docs: Documento[] | undefined) {
  const lista = docs ?? []
  const bloques = lista.map(bloqueDe)
  return {
    decidir: bloques.filter((i) => i === 0).length,
    preparar: bloques.filter((i) => i > 0).length,
    tramite: bloques.filter((i) => i < 0).length,
  }
}

/**
 * En qué bloque cae un documento; -1 = ninguno, o sea el pie de trámite y sin clasificar.
 *
 * ⚠ La CATEGORÍA manda, y se buscan TODAS las categorías antes de mirar ningún `grupo`. Con la
 * comprobación mezclada —«esta categoría o este grupo», bloque por bloque— el primer bloque
 * reclamaba por `grupo: licitacion` todos los «Anexo / formato» antes de que el segundo pudiera
 * reclamarlos por categoría, y Seguridad Social y Origen de Fondos acababan bajo «Para decidir si
 * te presentas». El `grupo` es solo la red para una categoría que no esté en ninguna lista.
 */
export function bloqueDe(d: Documento): number {
  const porCat = BLOQUES.findIndex((b) => b.cats.includes(d.categoria))
  if (porCat >= 0) return porCat
  return BLOQUES.findIndex((b) => b.grupos.includes(d.grupo || ''))
}



